import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { Role } from './auth';

/**
 * Staff presence: Clock in / Clock out (GET /presence/me, POST /presence/clock-in|clock-out), the "Who's working now"
 * board (GET /presence/now) and the attendance history (GET /presence/history). Every key starts with 'presence', so
 * the live stream (a clock-in anywhere the viewer may see) refreshes all of them at once.
 */
export interface MyPresence {
  businessDate: string;
  shift: { id: string; clockInAt: string; outletId: string | null; outletName: string | null } | null;
  todayMinutes: number;
  /** When the system closed my last shift because I forgot to clock out (null otherwise). */
  lastAutoClosedAt: string | null;
}

export type PresenceStatus = 'working' | 'idle' | 'off' | 'absent';

export interface BoardRow {
  userId: string;
  name: string;
  login: string;
  role: Role;
  status: PresenceStatus;
  groupId: string | null;
  groupName: string;
  groupKind: 'shop' | 'parking' | null;
  shiftId: string | null;
  clockedInAt: string | null;
  device: string | null;
  lastSeenAt: string | null;
  todayMinutes: number;
  shiftsToday: number;
  firstInToday: string | null;
  lastOutToday: string | null;
}

export interface Board {
  businessDate: string;
  activeMinutes: number;
  summary: Record<'total' | PresenceStatus, number>;
  staff: BoardRow[];
  generatedAt: string;
}

export interface ShiftRow {
  id: string;
  userId: string;
  name: string;
  login: string;
  role: Role;
  outletId: string | null;
  outletName: string | null;
  businessDate: string;
  clockInAt: string;
  clockOutAt: string | null;
  minutes: number;
  open: boolean;
  closedBy: 'self' | 'auto' | 'correction' | null;
  autoClosed: boolean;
  recordedClockOutAt: string | null;
  correctedAt: string | null;
  correctedByName: string | null;
  correctionReason: string | null;
  device: string | null;
}

export interface History {
  from: string;
  to: string;
  people: { userId: string; name: string; login: string; role: Role; outletName: string | null; minutes: number; daysWorked: number; shifts: number; autoClosed: number; corrected: number; openNow: boolean }[];
  days: { userId: string; name: string; day: string; minutes: number; shifts: number }[];
  shifts: ShiftRow[];
  totals: { people: number; shifts: number; minutes: number; autoClosed: number };
}

/** Roles that work shifts: they get the Clock in control and the "Clock in now?" offer after signing in. */
export const SHIFT_ROLES: readonly Role[] = ['security', 'cashier', 'venue_staff', 'shop_admin', 'shop_staff'];

export const STATUS_LABEL: Record<PresenceStatus, string> = {
  working: 'Working',
  idle: 'Clocked in, not active',
  off: 'Clocked out',
  absent: 'Not in today',
};

export function useMyPresence(enabled = true) {
  return useQuery({
    queryKey: ['presence', 'me'],
    queryFn: () => api<MyPresence>('/presence/me'),
    enabled,
    // The minutes worked tick up; the live stream refreshes it on clock-in/out elsewhere (another device).
    refetchInterval: 5 * 60_000,
    staleTime: 30_000,
  });
}

export function useClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { action: 'clock-in' | 'clock-out'; outletId?: string | null }) =>
      api<MyPresence>(`/presence/${v.action}`, { body: v.action === 'clock-in' && v.outletId ? { outletId: v.outletId } : {} }),
    onSuccess: (data) => {
      qc.setQueryData(['presence', 'me'], data);
      qc.invalidateQueries({ queryKey: ['presence'] });
    },
  });
}

export function usePresenceBoard(outletId?: string | null, enabled = true) {
  return useQuery({
    queryKey: ['presence', 'now', outletId ?? 'all'],
    queryFn: () => api<Board>('/presence/now', { query: { outletId: outletId || undefined } }),
    enabled,
    // "Working" depends on the last minutes of activity: re-check every minute (the stream covers clock-ins).
    refetchInterval: 60_000,
  });
}
